import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "utils/misc";
import colorsStore from "shared/store/colorsStore";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "rounded-full",
        login: "rounded-full border border-gray-600",
        warning: "",
        destructive: "rounded-full",
        outline: "border",
        grayOutlineSmall: "!py-1 !h-fit !pr-1 !pl-2 border rounded-full",
        grayOutline: "border rounded-full",
        destructiveOutline: "border rounded-full",
        secondary: "",
        ghost: "border border-gray-500",
        gray: "rounded-xl",
        link: "underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);
    const Comp = asChild ? Slot : "button";

    const getButtonColors = (
      variant: string | undefined,
    ): React.CSSProperties => {
      switch (variant) {
        case "default":
          return {
            backgroundColor: colorsConfig.main,
            color: colorsConfig.FinecoWhite,
          };
        case "login":
          return {
            backgroundColor: colorsConfig.gray800,
            color: colorsConfig.FinecoBlack,
          };
        case "warning":
          return {
            backgroundColor: colorsConfig.orange500,
            color: colorsConfig.white,
          };
        case "destructive":
          return {
            backgroundColor: colorsConfig.red500,
            color: colorsConfig.white,
          };
        case "outline":
          return {
            color: colorsConfig.green50,
            borderColor: colorsConfig.green500,
          };
        case "grayOutlineSmall":
        case "grayOutline":
          return {
            color: colorsConfig.light,
            borderColor: colorsConfig.light,
          };
        case "destructiveOutline":
          return {
            color: colorsConfig.red600,
            borderColor: colorsConfig.red500,
          };
        case "secondary":
          return {
            backgroundColor: colorsConfig.green100,
            color: colorsConfig.green900,
          };
        case "ghost":
          return { color: colorsConfig.green900 };
        case "gray":
          return {
            backgroundColor: colorsConfig.gray200,
            color: colorsConfig.gray600,
          };
        case "link":
          return { color: colorsConfig.green900 };
        default:
          return {
            backgroundColor: colorsConfig.main,
            color: colorsConfig.FinecoBlack,
          };
      }
    };

    return (
      <Comp
        className={
          cn(buttonVariants({ variant, size, className })) + " outline-none"
        }
        ref={ref}
        style={getButtonColors(variant)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
